import { DestroyRef, effect, inject, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { NavigationService } from '../services/navigation.service';
import { UpdateQueryParamsOptions } from '../tools/router-tools';
import { ObjectTools } from '../tools/object-tools';

export interface SolUrlHandlerConfig {
  /** Receives the params of the route this component renders: its own segment plus its ancestors'. */
  readonly pathParams?: (params: Readonly<Record<string, string>>) => void;
  /** Receives the URL's query params. */
  readonly queryParams?: (params: Readonly<Record<string, string>>) => void;
  /** Merges this signal's record into the query string whenever it actually changes. */
  readonly syncToUrl?: Signal<Record<string, any> | undefined>;
}

/**
 * A component's own interaction with the URL.
 *
 * Construct it as a field, so it captures the component's `ActivatedRoute` and `DestroyRef` from
 * the constructor's injection context — reads are scoped to the route the component renders by
 * construction, correct at construction time, and torn down with the component. Each watcher
 * fires immediately with the current value and again whenever the mapped params actually change.
 *
 * Path params identify the entity and are read-only here: changing them is a navigation, which
 * stays on `NavigationService`. Query params are page state and flow both ways — watch them via
 * `queryParams` and sync them back by handing `syncToUrl` a computed record (typically
 * `this.compute(state => state.toNavigationParams())`), so both directions live in one
 * declaration. {@link updateQueryParams} stays available for one-off writes.
 */
export class SolUrlHandler {
  private readonly route: ActivatedRoute;
  private readonly navigationService: NavigationService;

  constructor(config: SolUrlHandlerConfig) {
    const route = inject(ActivatedRoute, { optional: true });
    if (!route?.snapshot.routeConfig) {
      throw new Error('SolUrlHandler requires a routed context: pages own route params.');
    }
    this.route = route;
    this.navigationService = inject(NavigationService);
    const destroyRef = inject(DestroyRef);

    if (config.pathParams) {
      const consumer = config.pathParams;
      combineLatest(this.route.pathFromRoot.map(node => node.params))
        .pipe(
          map(segments => SolUrlHandler.mergeParams(segments)),
          distinctUntilChanged((previous, current) => ObjectTools.equals(previous, current)),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(params => consumer(params));
    }

    if (config.queryParams) {
      const consumer = config.queryParams;
      this.route.queryParams
        .pipe(
          distinctUntilChanged((previous, current) => ObjectTools.equals(previous, current)),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(params => consumer(params as Record<string, string>));
    }

    if (config.syncToUrl) {
      const source = config.syncToUrl;
      let previous: Record<string, any> | undefined;
      let synced = false;
      effect(() => {
        const params = source();
        if (!synced || !ObjectTools.equals(previous, params)) {
          previous = params;
          synced = true;
          this.updateQueryParams(params);
        }
      });
    }
  }

  /** Merge the given keys into the query string; a nullish value deletes its key. */
  public updateQueryParams(
    params: Record<string, any> | undefined,
    options: Partial<UpdateQueryParamsOptions> = {},
  ): void {
    this.navigationService.updateQueryParams(params, options);
  }

  private static mergeParams(segments: Params[]): Record<string, string> {
    const params: Record<string, string> = {};
    for (const segment of segments) {
      Object.assign(params, segment);
    }
    return params;
  }
}
